import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useWebsocketStore } from '../../../../../../../stores/websocketStore/websocketProvider';
import SoftOrderWrapper from '../../../../../tradeplans/softOrderModules/softOrderWrapper/SoftOrderWrapper';
import { lineComponentMap } from '../../../../../tradeplans/softOrderModules/tacticMap/componentMaps';
import { requestSoftOrdersPerTradePlan } from '../../../../../../../stores/websocketStore/utils/outgoingMessages/softorders/requestsoftorders/requestSoftOrdersPerTradePlan';
interface Props {
  tradePlanId: string;
  symbol?: string;
  size?: number;
  scaleY?: (value: number) => number;
  portalRef?: {
    current: any;
  };
}

const isSoftOrderDebugEnabled = () =>
  typeof window !== 'undefined' &&
  (window.config?.REACT_APP_DEBUG_SOFT_ORDERS === 'true' ||
    window.localStorage?.getItem('tuui.debugSoftOrders') === 'true');

const SoftOrdersContainer: React.FC<Props> = observer(
  ({ tradePlanId, ...rest }: Props) => {
    const { tradePlanStore } = useWebsocketStore();
    const softOrderArray: string[] =
      tradePlanStore.softOrderIdsByTradePlan[tradePlanId] ?? null;

    // Soft orders (priceToBreak, pendingTargets, etc.) are only pushed to the client when a tactic
    // actively configures/updates, or once on initial websocket connect - there's no channel that
    // resends them just because this chart mounted. Without this, a chart opened after the tactic's
    // one-time send already fired (e.g. the trade was entered before this tab/component existed)
    // would show no lines until a full page refresh re-established the websocket connection and
    // triggered the connect-time replay. Requesting explicitly on mount closes that gap.
    useEffect(() => {
      if (tradePlanId) {
        requestSoftOrdersPerTradePlan(Number(tradePlanId));
      }
    }, [tradePlanId]);

    if (isSoftOrderDebugEnabled()) {
      console.debug('[tuui:softOrders] render container', {
        at: new Date().toISOString(),
        tradePlanId,
        symbol: rest.symbol,
        ids: softOrderArray ? [...softOrderArray] : [],
      });
    }

    // Resolve position metrics once so every line component's HoverBox can show P&L.
    const metrics = tradePlanStore.getTradePlanPositionMetrics
      ? tradePlanStore.getTradePlanPositionMetrics(tradePlanId)
      : null;
    const tradePlan = tradePlanStore.tradePlans?.[tradePlanId];

    const positionMetrics = metrics
      ? {
          fillPrice: metrics.fillPrice ?? undefined,
          openQuantity: metrics.openQuantity ?? undefined,
          priceMultiplier: metrics.priceMultiplier ?? undefined,
          realizedPnl: metrics.realizedPnl ?? undefined,
          isLong: metrics.position?.sideLong ?? tradePlan?.goLong ?? true,
          tickSize: metrics.position?.tickSize ?? undefined,
          delta: metrics.position?.delta ?? undefined,
        }
      : {};

    return (
      <g>
        {softOrderArray &&
          softOrderArray.map((softOrderId) => {
            return (
              <SoftOrderWrapper
                tradePlanId={tradePlanId}
                key={softOrderId}
                softOrderId={softOrderId}
                componentMap={lineComponentMap}
                {...positionMetrics}
                {...rest}
              />
            );
          })}
      </g>
    );
  }
);

export default SoftOrdersContainer;
